/**
 * DELIV-17370 — client-side evaluator for the schedule preview.
 * Mirrors api--eqao/src/util/scoring/disallowed-time-ranges.ts (pure domain).
 *
 * Instant evaluation uses `Intl` to resolve the group's wall clock. Day/week
 * segments are derived analytically from the rule rows instead — a rule already
 * describes its blocked interval, so there is nothing to sample minute by minute.
 */

import {
  AssignedItemComponentType,
  IDisallowedTimeRange,
  IBlackoutPreviewGroup,
  parseMarkingTasks,
} from './types';

export type BlackoutEvalReason =
  | 'no_schedule'
  | 'no_active_ranges'
  | 'allow_all_day'
  | 'inside_blackout'
  | 'outside_blackout';

export interface BlackoutEvalResult {
  allowed: boolean;
  reason: BlackoutEvalReason;
  localTime?: string;
  rangeId?: number;
  groupKey?: string;
}

export interface BlackoutDaySegment {
  start: number;
  end: number;
  blocked: boolean;
}

export interface BlackoutSegmentLayer {
  groupKey: string;
  groupName?: string;
  color?: string;
}

export interface BlackoutCombinedSegment extends BlackoutDaySegment {
  /** Groups whose rules block during this slice (empty when available). */
  layers: BlackoutSegmentLayer[];
}

const WEEKDAY_MAP: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

const DOW_COLUMNS: (keyof IDisallowedTimeRange)[] = [
  'dow_7_sun',
  'dow_1_mon',
  'dow_2_tue',
  'dow_3_wed',
  'dow_4_thu',
  'dow_5_fri',
  'dow_6_sat',
];

const DEFAULT_TIMEZONE = 'America/Toronto';
const MINUTES_PER_DAY = 1440;

export const PREVIEW_GROUP_COLORS = [
  '#c0392b',
  '#2456c4',
  '#8e44ad',
  '#d35400',
  '#16a085',
  '#2c3e50',
];

function parseHm(hm?: string | null): number {
  if (!hm) return NaN;
  const [h, m] = String(hm).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
}

function resolveTz(timezone?: string | null): string {
  if (timezone === 'EST' || timezone === 'EDT') return DEFAULT_TIMEZONE;
  return timezone || DEFAULT_TIMEZONE;
}

/**
 * `Intl.DateTimeFormat` construction is expensive, and the preview resolves the
 * same handful of timezones repeatedly, so keep one formatter per zone.
 */
const FORMATTER_CACHE: Record<string, Intl.DateTimeFormat> = {};

function wallClockFormatter(timezone?: string | null): Intl.DateTimeFormat {
  const tz = resolveTz(timezone);
  if (!FORMATTER_CACHE[tz]) {
    FORMATTER_CACHE[tz] = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      hour12: false,
    });
  }
  return FORMATTER_CACHE[tz];
}

export function localWallClock(
  at: Date,
  timezone?: string | null,
): { minutes: number; day: number; date: string; label: string } {
  const partsList = wallClockFormatter(timezone).formatToParts(at);
  const parts: Record<string, string> = {};
  partsList.forEach((p) => { parts[p.type] = p.value; });
  const hour = Number(parts.hour === '24' ? 0 : parts.hour);
  const minute = Number(parts.minute);
  return {
    minutes: hour * 60 + minute,
    day: WEEKDAY_MAP[parts.weekday] ?? at.getUTCDay(),
    date: `${parts.year}-${parts.month}-${parts.day}`,
    label: `${parts.weekday} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
  };
}

function appliesToTaskType(range: IDisallowedTimeRange, taskType: string): boolean {
  return parseMarkingTasks(range.marking_tasks).includes(taskType);
}

function hasNoDowFlags(range: IDisallowedTimeRange): boolean {
  return !DOW_COLUMNS.some((col) => Number(range[col]) === 1);
}

/**
 * A rule with no weekday and no start date limits nothing, so "no weekday = every day" and
 * "no date = every date" together would read as always blocked. The editor refuses to save
 * such a rule and the API rejects it, so the preview must not paint it either.
 */
export function isSchedulableRange(range: IDisallowedTimeRange): boolean {
  return !hasNoDowFlags(range) || !!range.date_start;
}

function matchesDow(range: IDisallowedTimeRange, day: number): boolean {
  if (hasNoDowFlags(range)) return true;
  return Number(range[DOW_COLUMNS[day]]) === 1;
}

function matchesDateSpan(range: IDisallowedTimeRange, date: string): boolean {
  const start = range.date_start || null;
  const end = range.date_end || null;
  if (!start && !end) return true;
  if (start && date < start) return false;
  if (end && date > end) return false;
  if (start && !end && date !== start) return false;
  return true;
}

function isAllDayRange(range: IDisallowedTimeRange): boolean {
  const start = parseHm(range.time_start);
  const end = parseHm(range.time_end);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return true;
  return start === end;
}

function insideOwnDay(minutes: number, range: IDisallowedTimeRange): boolean {
  if (isAllDayRange(range)) return true;
  const start = parseHm(range.time_start);
  const end = parseHm(range.time_end);
  if (start < end) return minutes >= start && minutes < end;
  return minutes >= start;
}

function wrapsIntoNextDay(minutes: number, range: IDisallowedTimeRange): boolean {
  if (isAllDayRange(range)) return false;
  const start = parseHm(range.time_start);
  const end = parseHm(range.time_end);
  if (start <= end) return false;
  return minutes < end;
}

export interface BlackoutGroupEvalInput {
  group: IBlackoutPreviewGroup | null | undefined;
  at: Date;
  taskType?: string;
  isScheduleOn?: boolean;
}

/** Evaluate one preview group's rules at an instant. */
export function evaluateBlackoutGroup(input: BlackoutGroupEvalInput): BlackoutEvalResult {
  const { group, at } = input;
  const taskType = input.taskType || AssignedItemComponentType.SCORING;
  const groupKey = group?.key || 'default';

  if (input.isScheduleOn === false) {
    return { allowed: true, reason: 'no_schedule', groupKey };
  }
  if (!group) return { allowed: true, reason: 'no_schedule', groupKey };

  const ranges = (group.ranges || []).filter(Boolean).filter(isSchedulableRange);
  if (ranges.length === 0) {
    return { allowed: true, reason: 'no_active_ranges', groupKey };
  }

  const today = localWallClock(at, group.timezone);
  // Shift anchors are local calendar dates, including23/25-hour DST days.
  const yesterday = { date: dateAdd(today.date, -1), day: (today.day + 6) % 7 };

  const applicable = ranges.filter((r) => appliesToTaskType(r, taskType));
  if (applicable.length === 0) {
    return { allowed: true, reason: 'no_active_ranges', localTime: today.label, groupKey };
  }

  const allowAllDay = applicable.find(
    (r) =>
      Number(r.is_allow_all_day) === 1 &&
      matchesDateSpan(r, today.date) &&
      matchesDow(r, today.day),
  );
  if (allowAllDay) {
    return {
      allowed: true,
      reason: 'allow_all_day',
      localTime: today.label,
      rangeId: allowAllDay.id,
      groupKey,
    };
  }

  const blackouts = applicable.filter((r) => Number(r.is_allow_all_day) !== 1);

  const hitToday = blackouts.find(
    (r) =>
      matchesDateSpan(r, today.date) && matchesDow(r, today.day) && insideOwnDay(today.minutes, r),
  );
  if (hitToday) {
    return {
      allowed: false,
      reason: 'inside_blackout',
      localTime: today.label,
      rangeId: hitToday.id,
      groupKey,
    };
  }

  const hitYesterday = blackouts.find(
    (r) =>
      matchesDateSpan(r, yesterday.date) &&
      matchesDow(r, yesterday.day) &&
      wrapsIntoNextDay(today.minutes, r),
  );
  if (hitYesterday) {
    return {
      allowed: false,
      reason: 'inside_blackout',
      localTime: today.label,
      rangeId: hitYesterday.id,
      groupKey,
    };
  }

  return { allowed: true, reason: 'outside_blackout', localTime: today.label, groupKey };
}

/** Union across groups: blocked when any group blocks. */
export function evaluateBlackoutGroups(
  groups: IBlackoutPreviewGroup[],
  at: Date,
  taskType?: string,
  isScheduleOn = true,
): BlackoutEvalResult {
  if (!isScheduleOn || !groups?.length) {
    return { allowed: true, reason: 'no_schedule' };
  }
  const results = groups.map((group) =>
    evaluateBlackoutGroup({ group, at, taskType, isScheduleOn: true }),
  );
  const blocking = results.find((r) => !r.allowed);
  if (blocking) return blocking;
  return results[0] || { allowed: true, reason: 'no_active_ranges' };
}

/** A half-open [start, end) minute span within a single calendar day. */
export interface BlackoutInterval {
  start: number;
  end: number;
}

type Interval = BlackoutInterval;

function clampInterval(interval: Interval): Interval | null {
  const start = Math.max(0, Math.min(MINUTES_PER_DAY, interval.start));
  const end = Math.max(0, Math.min(MINUTES_PER_DAY, interval.end));
  return end > start ? { start, end } : null;
}

function mergeIntervals(intervals: Interval[]): Interval[] {
  const sorted = intervals
    .map(clampInterval)
    .filter((i): i is Interval => !!i)
    .sort((a, b) => a.start - b.start);

  const merged: Interval[] = [];
  for (const interval of sorted) {
    const last = merged[merged.length - 1];
    if (last && interval.start <= last.end) {
      last.end = Math.max(last.end, interval.end);
    } else {
      merged.push({ ...interval });
    }
  }
  return merged;
}

/** Weekday (0=Sun … 6=Sat) of a YYYY-MM-DD calendar date, without `Intl`. */
export function dayOfWeekForDate(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/**
 * Blocked intervals for one group on one calendar date, in that group's own
 * local time. Derived straight from the rule rows, mirroring the precedence in
 * `evaluateBlackoutGroup`: an allow-all-day override clears the whole day,
 * otherwise same-day slices and the previous day's overnight wrap are unioned.
 */
export function blockedIntervalsForGroup(
  group: IBlackoutPreviewGroup,
  dateStr: string,
  taskType: string,
): Interval[] {
  const ranges = (group?.ranges || []).filter(Boolean).filter(isSchedulableRange);
  if (!ranges.length) return [];

  const applicable = ranges.filter((r) => appliesToTaskType(r, taskType));
  if (!applicable.length) return [];

  const day = dayOfWeekForDate(dateStr);

  const allowAllDay = applicable.some(
    (r) =>
      Number(r.is_allow_all_day) === 1 &&
      matchesDateSpan(r, dateStr) &&
      matchesDow(r, day),
  );
  if (allowAllDay) return [];

  const blackouts = applicable.filter((r) => Number(r.is_allow_all_day) !== 1);
  const intervals: Interval[] = [];

  for (const range of blackouts) {
    if (!matchesDateSpan(range, dateStr) || !matchesDow(range, day)) continue;
    if (isAllDayRange(range)) {
      intervals.push({ start: 0, end: MINUTES_PER_DAY });
      continue;
    }
    const start = parseHm(range.time_start);
    const end = parseHm(range.time_end);
    // An end before the start runs overnight; only its evening side lands today.
    intervals.push(start < end ? { start, end } : { start, end: MINUTES_PER_DAY });
  }

  const yesterday = dateAdd(dateStr, -1);
  const yesterdayDow = dayOfWeekForDate(yesterday);
  for (const range of blackouts) {
    if (isAllDayRange(range)) continue;
    if (!matchesDateSpan(range, yesterday) || !matchesDow(range, yesterdayDow)) continue;
    const start = parseHm(range.time_start);
    const end = parseHm(range.time_end);
    if (start > end) intervals.push({ start: 0, end });
  }

  return mergeIntervals(intervals);
}

/** Minutes a timezone sits ahead of UTC on a given calendar date. */
function timezoneOffsetMinutes(dateStr: string, timezone?: string | null): number {
  const [y, m, d] = dateStr.split('-').map(Number);
  const probe = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const wall = localWallClock(probe, timezone);
  const dayDelta = wall.date < dateStr ? -1 : wall.date > dateStr ? 1 : 0;
  return dayDelta * MINUTES_PER_DAY + wall.minutes - 12 * 60;
}

/** Shift intervals from a group's local clock onto the display clock, wrapping at midnight. */
function shiftIntervals(intervals: Interval[], deltaMinutes: number): Interval[] {
  if (!deltaMinutes) return intervals;
  const shifted: Interval[] = [];
  for (const interval of intervals) {
    const start = interval.start + deltaMinutes;
    const end = interval.end + deltaMinutes;
    shifted.push({ start, end });
    shifted.push({ start: start - MINUTES_PER_DAY, end: end - MINUTES_PER_DAY });
    shifted.push({ start: start + MINUTES_PER_DAY, end: end + MINUTES_PER_DAY });
  }
  return mergeIntervals(shifted);
}

/**
 * Combined segments with layer metadata for the overlapping preview.
 *
 * Boundaries come from the rule intervals themselves, so the cost scales with
 * the number of rules rather than the 1440 minutes in a day.
 */
export function combinedDaySegments(
  groups: IBlackoutPreviewGroup[],
  dateStr: string,
  taskType: string,
  isScheduleOn = true,
): BlackoutCombinedSegment[] {
  if (!isScheduleOn || !groups?.length) {
    return [{ start: 0, end: MINUTES_PER_DAY, blocked: false, layers: [] }];
  }

  const normalized = normalizePreviewGroups(groups);
  const displayTz = normalized[0]?.timezone;
  const displayOffset = timezoneOffsetMinutes(dateStr, displayTz);

  const perGroup = normalized.map((group) => {
    const intervals = blockedIntervalsForGroup(group, dateStr, taskType);
    // A wall-clock time in the group's zone lands on the display axis shifted by
    // the offset difference — a group further west shows up later in the day.
    const delta = group.timezone === displayTz
      ? 0
      : displayOffset - timezoneOffsetMinutes(dateStr, group.timezone);
    return {
      layer: { groupKey: group.key, groupName: group.name, color: group.color },
      intervals: shiftIntervals(intervals, delta),
    };
  });

  const boundaries = new Set<number>([0, MINUTES_PER_DAY]);
  perGroup.forEach(({ intervals }) => {
    intervals.forEach(({ start, end }) => {
      boundaries.add(start);
      boundaries.add(end);
    });
  });

  const points = Array.from(boundaries).sort((a, b) => a - b);
  const segments: BlackoutCombinedSegment[] = [];

  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i + 1];
    if (end <= start) continue;
    const midpoint = (start + end) / 2;
    const layers = perGroup
      .filter(({ intervals }) =>
        intervals.some((iv) => midpoint >= iv.start && midpoint < iv.end))
      .map(({ layer }) => layer);

    const previous = segments[segments.length - 1];
    const sameLayers = previous
      && previous.layers.length === layers.length
      && previous.layers.every((l, idx) => l.groupKey === layers[idx].groupKey);

    if (sameLayers) previous.end = end;
    else segments.push({ start, end, blocked: layers.length > 0, layers });
  }

  return segments;
}

export function blockedMinutes(segments: BlackoutDaySegment[]): number {
  return segments.filter((s) => s.blocked).reduce((a, s) => a + (s.end - s.start), 0);
}

/**
 * Blocked share as display text, e.g. `41.7%`. Never empty: the template used to pipe a number
 * through DecimalPipe, which renders nothing for NaN / null.
 */
export function formatBlockedPct(blocked: number, total: number): string {
  const pct = total > 0 ? (Number(blocked) / total) * 100 : 0;
  if (!Number.isFinite(pct)) return '0%';
  const rounded = Math.round(Math.min(100, Math.max(0, pct)) * 10) / 10;
  return `${rounded}%`;
}

export function formatHm(minutes: number): string {
  if (minutes >= MINUTES_PER_DAY) return '24:00';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

export const PREVIEW_WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const PREVIEW_WEEKDAY_FULL = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

/** ISO date string for today in a timezone. */
export function todayInTimezone(timezone?: string | null): string {
  return localWallClock(new Date(), timezone).date;
}

/** Sunday-start week containing `dateStr`. */
export function weekStartOf(dateStr: string): string {
  return dateAdd(dateStr, -dayOfWeekForDate(dateStr));
}

export function dateAdd(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function normalizePreviewGroups(
  groups: IBlackoutPreviewGroup[] | null | undefined,
): IBlackoutPreviewGroup[] {
  return (groups || []).map((g, i) => ({
    ...g,
    key: g.key || String(g.id ?? i),
    color: g.color || PREVIEW_GROUP_COLORS[i % PREVIEW_GROUP_COLORS.length],
  }));
}
